import React, { ReactNode } from 'react';

import styled from 'styled-components';

const StyledPageLinkFeatureSection = styled.div<{ $mobileBackground: string }>`
  padding: 150px 0;
  background-color: var(--white);

  @media (max-width: 768px) {
    padding: 60px 0 60px 0;
    background-color: ${({ $mobileBackground }) => $mobileBackground};
  }

  .content {
    border-radius: 25.8px;
    overflow: hidden;
    box-shadow: 0px 531.667px 149.028px 0px rgba(0, 0, 0, 0),
      0px 339.944px 136.139px 0px rgba(0, 0, 0, 0.01),
      0px 191.722px 115.194px 0px rgba(0, 0, 0, 0.03), 0px 85.389px 85.389px 0px rgba(0, 0, 0, 0.04),
      0px 20.944px 46.722px 0px rgba(0, 0, 0, 0.05);

    @media (max-width: 768px) {
      box-shadow: none;
      background-color: ${({ $mobileBackground }) => $mobileBackground};
      border-radius: 0;
      overflow: visible;
    }

    > div {
      padding: 60px;
      border-radius: 25.8px;
      border: 0.806px solid var(--stile-grey-1);
      background-color: var(--stile-grey-1);
      position: relative;
      overflow: hidden;

      display: flex;
      flex-direction: row;
      gap: 46px;

      @media (max-width: 768px) {
        background-color: ${({ $mobileBackground }) => $mobileBackground};
        flex-direction: column-reverse;
        padding: 0;
        gap: 20px;
        border: none;
        border-radius: 0;
        overflow: visible;
      }

      .--left,
      .--right {
        flex-basis: 50%;
        @media (max-width: 768px) {
          flex-basis: 100%;
        }
      }

      .--left {
        align-content: center;
        .--headings {
          display: flex;
          flex-direction: column;
          gap: 22px;
          .h9 {
            margin: 0;
            padding: 0;
          }
          h4 {
            margin: 0;
            padding: 0;
            text-wrap: balance;
          }
        }
        .--copy {
          display: flex;
          flex-direction: column;
          margin: 25px 0 0 0;
          @media (max-width: 768px) {
            margin: 12px 0 0 0;
          }
          .--buttons {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
          p {
            width: 100%;
            max-width: 425px;
            padding: 0;
            margin: 0 0 12px;

            &:last-of-type {
              margin-bottom: 32px;
            }

            @media (max-width: 768px) {
              max-width: none;
              width: 100%;
            }
          }
          .button {
            div {
              margin-left: 6px;
            }
            &.mobile {
              display: none;

              @media (max-width: 768px) {
                display: flex;
              }
            }

            @media (max-width: 768px) {
              &.desktop {
                display: none;
              }
            }
          }
        }
      }

      .--right {
        .gatsby-image-wrapper {
          border-radius: 32px;
          @media (max-width: 768px) {
            width: 100%;
            border-radius: 20px;
          }
        }
      }
    }
  }
`;

type PageLinkFeatureProps = {
  heading: string;
  copy: string | ReactNode;
  button: ReactNode;
  image: ReactNode;
  /**
   * Background colour applied to the mobile (≤768px) layout. The mobile design
   * is "flush" — the section, the inner content wrapper, and the inner card div
   * all share this colour. Defaults to `var(--white)`.
   */
  mobileBackground?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export default function PageLinkFeature({
  heading,
  copy,
  button,
  image,
  className,
  mobileBackground = 'var(--white)',
  ...rest
}: PageLinkFeatureProps) {
  return (
    <StyledPageLinkFeatureSection
      className={['page-link-feature-section', className].filter(Boolean).join(' ')}
      $mobileBackground={mobileBackground}
      {...rest}
    >
      <div className="content">
        <div>
          <div className="--left">
            <div className="--headings">
              <h4>{heading}</h4>
            </div>
            <div className="--copy">
              {typeof copy === 'string' ? <p>{copy}</p> : copy}
              <div className="--buttons">{button}</div>
            </div>
          </div>
          <div className="--right">{image}</div>
        </div>
      </div>
    </StyledPageLinkFeatureSection>
  );
}
